/**
 * A player's nights, newest first, each one a way into its own summary.
 *
 * THE ENTRY ID IS WHY THESE ROWS CAN LINK ANYWHERE. A tournament summary is
 * scoped to one player's card, so its route needs the entry that played rather
 * than the account — and getPlayerTournamentHistory carries entryId on every row
 * for exactly this, so the link needs no second lookup.
 *
 * A row with no entryId is rendered as plain text rather than a dead link. That
 * should not arise — the id comes from the stats row's own entry — but a link
 * that looks live and 404s is worse than a line that does not offer one.
 */

import { Link } from 'react-router-dom';
import { formatDateShort } from '../../lib/format.js';
import { placementLabel, placementBadgeClass, tournamentLabel } from '../../lib/placement.js';

function HistoryRow({ venueSlug, row }) {
  const inner = (
    <>
      <span className="hd-drill-date">{formatDateShort(row.date) ?? '—'}</span>
      <span className="hd-drill-name">
        {tournamentLabel(row.nightNumber)}
        {row.seasonName && <span className="hd-muted"> · {row.seasonName}</span>}
      </span>
      <span className={placementBadgeClass(row.placement)}>
        {placementLabel(row.placement)}
      </span>
      <span className="hd-drill-pts">
        {row.placementPoints} pt{row.placementPoints === 1 ? '' : 's'}
      </span>
    </>
  );

  if (row.entryId == null) {
    return <div className="hd-drill-row">{inner}</div>;
  }

  return (
    <Link
      className="hd-drill-row hd-drill-link"
      to={`/venues/${venueSlug}/tournaments/${row.tournamentId}/player/${row.entryId}`}
    >
      {inner}
    </Link>
  );
}

export default function TournamentHistoryList({ venueSlug, tournaments }) {
  if (!tournaments?.length) {
    return (
      <div className="hd-empty">
        No completed tournaments yet — a night appears here once it is finished.
      </div>
    );
  }

  return (
    <div className="hd-drill-list">
      {tournaments.map((row) => (
        <HistoryRow key={row.tournamentId} venueSlug={venueSlug} row={row} />
      ))}
    </div>
  );
}
